import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Stars from "../components/Stars";
import { obterPainel, responderDenuncia, listarRecrutadores, convidarRecrutador, reenviarConviteRecrutador, reenviarConvitesPendentes } from "../services/api";
import { useAuth } from "../context/AuthContext";
import PreferenciasContato from "../components/PreferenciasContato";
import ConectorAts from "../components/ConectorAts";
import AvisosEtapa from "../components/AvisosEtapa";
import DadosEmpresa from "../components/DadosEmpresa";
import CandidaturasEmpresa from "../components/CandidaturasEmpresa";

function fmt(iso) { try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return ""; } }

export default function PainelPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  // O painel abre nos RELATOS: é a razão de a empresa existir aqui — responder constrói a reputação dela.
  // Preferências, recrutadores e conector são configuração: mexe-se uma vez e não se volta. Antes vinham
  // primeiro e empurravam os relatos para baixo da dobra, o que fazia o link "Relatos" cair na tela errada.
  const [aba, setAba] = useState("relatos");
  // Sub-aba de Configurações. Cinco blocos empilhados viravam rolagem: quem entrava para mexer no conector
  // passava por dados da empresa, preferências e recrutadores até achar. Separar deixa cada assunto a um
  // clique e, principalmente, deixa VISÍVEL que os assuntos existem — antes, o que ficava embaixo da dobra
  // era o que ninguém descobria.
  const [sub, setSub] = useState("empresa");
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    try { setDados(await obterPainel()); }
    catch (e) { setErro(e.message); }
  }, []);

  useEffect(() => {
    if (!user || (user.tipo !== 1 && !isAdmin)) { navigate("/entrar"); return; }
    carregar();
  }, [user, isAdmin, navigate, carregar]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-8 pb-28 md:pb-8">
        <h1 className="text-2xl font-bold text-text mb-1">Painel da empresa</h1>
        <p className="text-gray-600 mb-6">Acompanhe e responda aos relatos da sua empresa.</p>

        {erro && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{erro}</div>}
        {!dados && !erro && <p className="text-gray-500">Carregando...</p>}

        <div className="mb-6 flex gap-1 border-b border-gray-200">
          {/* Candidaturas fica NO MEIO de propósito: relatos é a razão de a empresa existir aqui, mas quem
              tem vaga no ar entra para ver gente, não para ver configuração. */}
          {[["relatos", "Relatos"], ["candidaturas", "Candidaturas"], ["config", "Configurações"]].map(([k, t]) => (
            <button key={k} onClick={() => setAba(k)}
              className={`px-4 py-2 text-sm font-semibold rounded-t-lg ${aba === k
                ? "bg-white border border-gray-200 border-b-white text-primary -mb-px"
                : "text-gray-500 hover:text-gray-800"}`}>{t}</button>
          ))}
        </div>

        {aba === "candidaturas" && <CandidaturasEmpresa />}

        {aba === "config" && (<>
          {/* Rolagem horizontal no celular em vez de quebrar em duas linhas: sub-aba que quebra parece menu
              bagunçado e some no meio da tela. */}
          <div className="mb-5 flex gap-2 overflow-x-auto pb-1">
            {[["empresa", "Dados da empresa"], ["contato", "Contato"], ["equipe", "Equipe"],
              ["conector", "Integração"], ["avisos", "Avisar o candidato"]].map(([k, t]) => (
              <button key={k} onClick={() => setSub(k)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-semibold border ${sub === k
                  ? "bg-primary text-white border-primary"
                  : "bg-white text-gray-600 border-gray-200 hover:text-gray-900"}`}>{t}</button>
            ))}
          </div>

          {/* Os dados da empresa moram em /conta, junto dos dados pessoais de quem está logado — e ninguém
              procura o cadastro da EMPRESA dentro do menu da própria conta. Enquanto não são duas telas
              separadas, o atalho fica aqui, que é onde a empresa vem quando quer configurar alguma coisa. */}
          {/* Os campos vêm direto, sem passar por um botão que leva a outra tela: clicar numa aba para
              achar outro botão é um passo a mais sem motivo. O mesmo formulário continua existindo em /conta,
              pelo mesmo componente — duplicar garantiria que um dos dois ficasse para trás. */}
          {sub === "empresa" && <div className="mb-6"><DadosEmpresa /></div>}
          {sub === "contato" && <div className="mb-6"><PreferenciasContato /></div>}
          {sub === "equipe" && <div className="mb-6"><RecrutadoresEmpresa /></div>}
          {sub === "conector" && <div className="mb-6"><ConectorAts /></div>}
          {sub === "avisos" && <div className="mb-6"><AvisosEtapa /></div>}
        </>)}

        {aba === "relatos" && dados && (
          <>
            {dados.empresa.verificada === false && (
              <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-800 text-sm">
                <b>⏳ Empresa em análise.</b> Sua conta foi recebida e aguarda aprovação do eaiRH. Você já acompanha os relatos, mas só poderá <b>responder</b> após a verificação (confirmação do vínculo com a empresa).
              </div>
            )}
            {/* Onboarding: empresa aprovada com reclamações sem resposta → chama direto pra ação (constrói reputação). */}
            {dados.empresa.verificada !== false && dados.pendentes > 0 && (
              <div className="mb-6 rounded-xl border border-orange-300 bg-orange-50 p-4 text-orange-900 text-sm">
                <b>👋 Você tem {dados.pendentes} relato(s) aguardando sua resposta.</b> Responder — mesmo que para esclarecer — <b>constrói a sua reputação</b> no eaiRH e melhora seu índice. Comece pelos destacados abaixo. 👇
              </div>
            )}
            {/* Cartões de resumo */}
            {/* Elogio ganhou cartão próprio: sem ele, um relato positivo aparecia como "resolvida",
                que é vocabulário de problema encerrado — e some do lugar onde a empresa se orgulharia. */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
              <Card v={dados.elogios ?? 0} l="elogios" cor="text-emerald-600" destaque={(dados.elogios ?? 0) > 0} />
              <Card v={dados.pendentes} l="pendentes" cor="text-orange-600" destaque={dados.pendentes > 0} />
              <Card v={dados.respondidas} l="respondidas" cor="text-blue-600" />
              <Card v={dados.contestadas} l="contestadas" cor="text-yellow-600" />
              <Card v={dados.resolvidas} l="resolvidas" cor="text-green-600" />
              <Card v={dados.prazoExpirado} l="prazo expirado" cor="text-red-600" destaque={dados.prazoExpirado > 0} />
            </div>

            {/* Reputação resumida */}
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm mb-6 flex flex-wrap items-center gap-6">
              <div className="text-center">
                {dados.empresa.selo === "none" ? (
                  <div className="max-w-[180px]">
                    <div className="text-xl font-extrabold text-gray-700 leading-tight">Reputação em formação</div>
                    <div className="text-xs text-gray-500 mt-1">a nota aparece conforme os candidatos avaliam</div>
                  </div>
                ) : (
                  <>
                    <div className="text-4xl font-extrabold text-primary">{dados.empresa.score.toFixed(1)}</div>
                    {dados.denuncias.length > 0 && <div className="mt-1"><Stars score={dados.empresa.score} size={18} /></div>}
                    <div className="text-xs text-gray-500 mt-0.5">índice eaiRH</div>
                  </>
                )}
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <div><b>{dados.empresa.classificacao}</b></div>
                {/* Mesma regra da página pública: sem relato que peça resposta, "0% respondidas" não tem base. */}
                {/* "sem avaliações ainda" só quando REALMENTE não houve avaliação — antes dependia do selo,
                    e uma empresa avaliada com nota 8 continuava lendo que ninguém a tinha avaliado. */}
                <div>{dados.empresa.totalDenuncias > 0 ? `${dados.empresa.indiceResposta}% respondidas` : "nenhum relato aguardando resposta"}{dados.empresa.notaMedia > 0 ? ` · nota média ${dados.empresa.notaMedia.toFixed(1)}` : " · sem avaliações ainda"}</div>
                {dados.empresa.tempoMedioRetornoDias != null && (
                  <div>⏱ tempo médio de retorno: {dados.empresa.tempoMedioRetornoDias} dia(s)</div>
                )}
              </div>
            </div>

            {/* Lista de denúncias com ação rápida de resposta */}
            {/* Âncora do link "Relatos" da barra: scroll-mt evita que o cabeçalho fixo cubra o título. */}
            <h2 id="relatos" className="scroll-mt-24 text-lg font-bold text-gray-900 mb-3">Relatos</h2>
            <div className="space-y-3">
              {[...dados.denuncias]
                .sort((a, b) => {
                  const ap = a.resposta ? 1 : 0, bp = b.resposta ? 1 : 0; // sem resposta primeiro
                  if (ap !== bp) return ap - bp;
                  return new Date(b.criadoEm) - new Date(a.criadoEm);      // depois, mais recentes
                })
                .map((d) => (
                  <DenunciaPainel key={d.id} d={d} onAcao={carregar} podeResponder={dados.empresa.verificada !== false} />
                ))}
              {dados.denuncias.length === 0 && (
                <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-gray-500">
                  Nenhum relato registrado para sua empresa. 🎉
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Card({ v, l, cor, destaque }) {
  return (
    <div className={`rounded-xl border bg-white p-4 text-center ${destaque ? "border-orange-300 ring-1 ring-orange-200" : "border-gray-200"}`}>
      <div className={`text-2xl font-bold ${cor}`}>{v}</div>
      <div className="text-xs text-gray-500 mt-0.5">{l}</div>
    </div>
  );
}

function DenunciaPainel({ d, onAcao, podeResponder = true }) {
  const [resp, setResp] = useState(false);
  const [texto, setTexto] = useState("");
  const [erro, setErro] = useState(null);
  const [enviando, setEnviando] = useState(false);

  return (
    <article className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900">{d.titulo}</h3>
          <p className="text-xs text-gray-500">{d.vaga ? `${d.vaga} · ` : ""}{d.etapaTexto} · {fmt(d.criadoEm)} · por {d.autorNome}</p>
          {d.candidatoNome && (
            <p className="text-xs mt-1 inline-block rounded-lg bg-blue-50 border border-blue-100 px-2 py-1 text-blue-900">
              👤 Candidato: <b>{d.candidatoNome}</b> <span className="text-blue-700/80">— visível só para a empresa, para localizar o processo internamente</span>
            </p>
          )}
        </div>
        <span className="text-white text-xs font-semibold rounded-full px-2.5 py-1 shrink-0" style={{ backgroundColor: d.statusCor }}>
          {d.statusTexto}
        </span>
      </div>
      <p className="mt-2 text-sm text-gray-700">{d.descricao}</p>

      {d.prazoExpirado && !d.resposta && (
        <p className="mt-2 text-xs font-semibold text-red-600">⏰ Prazo de {d.prazoRespostaDias} dias expirado</p>
      )}

      {/* Mesma informação vista pelo lado da empresa: aqui quem lê é ela, então o texto fala do convite recebido. */}
      {d.empresaContatadaEm && !d.resposta && (
        <p className="mt-1 text-xs font-semibold text-blue-700">✉️ Convite ao direito de resposta enviado em {fmt(d.empresaContatadaEm)}</p>
      )}

      {d.resposta && (
        <div className="mt-2 border-l-4 border-primary bg-blue-50 rounded-r-lg p-2.5 text-sm text-gray-700">
          <b className="text-primary text-xs block">Sua resposta</b>{d.resposta.texto}
        </div>
      )}
      {d.contestacaoTexto && (
        <div className="mt-2 border-l-4 border-yellow-500 bg-yellow-50 rounded-r-lg p-2.5 text-sm text-gray-700">
          <b className="text-yellow-700 text-xs block">Réplica do candidato</b>{d.contestacaoTexto}
        </div>
      )}

      {!d.resposta && podeResponder && (
        resp ? (
          <div className="mt-3">
            {erro && <p role="alert" className="text-red-600 text-sm mb-1">{erro}</p>}
            <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows="3"
              className="w-full rounded-lg border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-primary outline-none"
              placeholder="Escreva sua resposta pública..." />
            <div className="flex gap-2 mt-2">
              <button
                disabled={enviando}
                onClick={async () => { if (enviando) return; setEnviando(true); try { await responderDenuncia(d.id, texto); onAcao(); } catch (e) { setErro(e.message); setEnviando(false); } }}
                className="px-4 py-1.5 rounded-lg bg-primary disabled:opacity-60 text-white text-sm font-semibold">{enviando ? "Publicando..." : "Publicar"}</button>
              <button onClick={() => setResp(false)} className="px-4 py-1.5 rounded-lg border text-gray-600 text-sm">Cancelar</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setResp(true)}
            className="mt-3 px-3 py-1.5 rounded-lg border border-primary text-primary text-sm font-semibold hover:bg-primary hover:text-white transition">
            Responder
          </button>
        )
      )}
    </article>
  );
}

// Recrutadores da empresa (multiusuário): a empresa convida membros que operam EM NOME dela.
function RecrutadoresEmpresa() {
  const [lista, setLista] = useState(null);
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState(null);
  const [erro, setErro] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [reenviando, setReenviando] = useState(null);
  const [reenviandoTodos, setReenviandoTodos] = useState(false);
  const carregar = () => listarRecrutadores().then(setLista).catch(() => setLista([]));
  useEffect(() => { carregar(); }, []);
  const reenviarTodos = async () => {
    setReenviandoTodos(true); setMsg(null); setErro(null);
    try {
      const r = await reenviarConvitesPendentes();
      if (r.falharam > 0) setErro(`${r.enviados} enviado(s), mas falhou para: ${(r.nomes || []).join(", ")}.`);
      else setMsg(r.aviso || `Convite reenviado para ${r.enviados} pessoa(s).`);
    } catch (e) { setErro(e.message); }
    finally { setReenviandoTodos(false); }
  };

  const reenviar = async (r) => {
    setReenviando(r.id); setMsg(null); setErro(null);
    try {
      const res = await reenviarConviteRecrutador(r.id);
      if (res?.ok === false) setErro(res.erro || "O e-mail não saiu. Tente de novo.");
      else setMsg(`Convite reenviado para ${r.email}.`);
    } catch (e) { setErro(e.message); }
    finally { setReenviando(null); }
  };

  const convidar = async (e) => {
    e.preventDefault(); setMsg(null); setErro(null); setEnviando(true);
    try {
      await convidarRecrutador(nome.trim(), email.trim());
      setMsg("Convite enviado. A pessoa recebe um e-mail para definir a senha e já entra em nome da empresa.");
      setNome(""); setEmail(""); carregar();
    } catch (err) { setErro(err.message); }
    finally { setEnviando(false); }
  };
  const inp = "rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary";
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="font-bold text-gray-900">Recrutadores da empresa</h2>
      <p className="text-sm text-gray-500 mt-1">Adicione pessoas do seu time. Elas publicam vagas, buscam candidatos e respondem em nome da empresa.</p>
      <form onSubmit={convidar} className="mt-3 flex flex-wrap gap-2">
        <input className={inp} placeholder="Nome" value={nome} onChange={(e) => setNome(e.target.value)} required />
        <input className={inp} type="email" placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <button disabled={enviando} className="rounded-xl bg-primary hover:bg-brand disabled:opacity-60 text-white font-semibold px-4 py-2 text-sm">{enviando ? "Enviando..." : "Convidar recrutador"}</button>
      </form>
      {/* Um botão para todos: a empresa abre o painel, vê três ou quatro parados e não vai clicar um a um —
          e é clicando um a um que ela reenvia duas vezes para a mesma pessoa. */}
      {lista && lista.some((r) => r.pendente) && (
        <button type="button" onClick={reenviarTodos} disabled={reenviandoTodos}
          className="mt-3 rounded-xl border border-primary/40 text-primary hover:bg-primary/5 disabled:opacity-50 text-sm font-semibold px-4 py-2">
          {reenviandoTodos ? "Reenviando..." : `Reenviar convite para os ${lista.filter((r) => r.pendente).length} pendentes`}
        </button>
      )}
      {msg && <p className="text-green-700 text-sm mt-2">{msg}</p>}
      {erro && <p className="text-red-600 text-sm mt-2">{erro}</p>}
      {lista && lista.length > 0 && (
        <ul className="mt-4 divide-y divide-gray-100 text-sm">
          {lista.map((r) => (
            <li key={r.id} className="py-2 flex items-center justify-between gap-2">
              <span><span className="font-medium text-gray-800">{r.nome}</span> <span className="text-gray-500">· {r.email}</span></span>
              {r.pendente && (
                <span className="flex items-center gap-2 shrink-0">
                  <span className="text-xs rounded-full bg-amber-100 text-amber-800 px-2 py-0.5">convite pendente</span>
                  {/* O link de acesso vale 7 dias. Sem reenvio, convite expirado (ou perdido no spam) deixa a
                      pessoa "pendente" para sempre e a empresa sem nenhuma ação possível. */}
                  <button type="button" onClick={() => reenviar(r)} disabled={reenviando === r.id}
                    className="text-xs font-semibold text-primary hover:underline disabled:opacity-50">
                    {reenviando === r.id ? "reenviando..." : "reenviar"}
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
